import { useState, useEffect } from 'react';
import { BUILT_IN_MEDICINE_CATEGORY, MS_PER_DAY, RECORDS_DEFAULT_DAYS, RECORDS_RANGE_OPTIONS, TEMP_MAX, TEMP_MIN, recordsRangeLabel } from '../constants.js';
import { parseStoredConcentration } from '../rules/concentrations.js';
import { notify } from '../lib/notify.js';
import { capitalizeMed, dayHeading, dayKey, formatAgo, formatTimePart, formatTimestamp, loggedByLabel, parseEnteredTime, toLocalDatetimeValue } from '../lib/format.js';
import { Book, Pencil, Save, Trash2, X } from '../components/icons.jsx';
import { TemperatureChart } from '../components/TemperatureChart.jsx';

export function HistoryView({darkMode, dosageHistory, temperatureLog, childrenList, otherMedications, onDelete, onDeleteTemperature, onRestore, onRestoreTemperature, onUpdate, onUpdateTemperature, undoStack, onUndo, defaultChildId}) {
  // Every card now states how long ago it was, and the undo banner counts
  // minutes, so the list has to keep moving while the screen sits open —
  // otherwise "2 min ago" is still claiming that an hour later. Same half-minute
  // tick as CalculatorView's banner.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setClockTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // The banner's child picker is the child filter: on this tab its empty
  // choice reads "All children".
  const filterChild = defaultChildId || 'all';
  // The timeline folds away to let the list start higher; remembered per phone.
  const [timelineOpen, setTimelineOpenState] = useState(() => {
    try { return localStorage.getItem('medTimelineOpen') === 'true'; } catch { return false; }
  });
  const setTimelineOpen = (open) => {
    setTimelineOpenState(open);
    try { localStorage.setItem('medTimelineOpen', String(open)); } catch (e) {}
  };
  // Multi-select: empty means no filter (show every medicine).
  const [selectedMedicines, setSelectedMedicines] = useState([]);
  const toggleMedicine = (key) => {
    setSelectedMedicines(prev => prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]);
  };
  const toggleCategory = (keys) => {
    setSelectedMedicines(prev => {
      const allSelected = keys.every(k => prev.includes(k));
      return allSelected ? prev.filter(k => !keys.includes(k)) : [...new Set([...prev, ...keys])];
    });
  };
  // Controls temperature readings in both the on-screen list and Export.
  // Temperature is shown by default, so this only tracks the opt-out (an
  // unchecked box is the default/no-filter state and shouldn't count as active).
  const [hideTemperature, setHideTemperature] = useState(false);
  const showTemperature = !hideTemperature;
  const [showFilters, setShowFilters] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [filterDays, setFilterDays] = useState(RECORDS_DEFAULT_DAYS);
  const [exportDays, setExportDays] = useState('30');
  const [exportFormat, setExportFormat] = useState('text');

  // Every "Other" medicine name that's either configured in Settings or shows
  // up in existing history (covers names since renamed or removed there), with
  // its category — falling back to "Other" for names no longer in Settings.
  const categoryByName = Object.fromEntries(otherMedications.map(m => [m.name, m.category || 'Other']));
  const otherMedicineNames = [...new Set([
    ...otherMedications.map(m => m.name),
    ...dosageHistory.filter(e => e.medication === 'other').map(e => e.medicationType),
  ])].sort();

  const medicineLabel = (m) => m === 'paracetamol' ? 'Paracetamol' : m === 'ibuprofen' ? 'Ibuprofen' : m;

  // Two-tiered grouping for the filter UI: category → its medicines. The two
  // built-in medicines are grouped by their fixed category, then each "Other"
  // medicine falls under its own category.
  const medicineGroups = {};
  Object.entries(BUILT_IN_MEDICINE_CATEGORY).forEach(([key, cat]) => {
    (medicineGroups[cat] = medicineGroups[cat] || []).push({ key, label: medicineLabel(key) });
  });
  otherMedicineNames.forEach(name => {
    const cat = categoryByName[name] || 'Other';
    (medicineGroups[cat] = medicineGroups[cat] || []).push({ key: name, label: name });
  });

  const matchesMedicineFilter = (e) => {
    if (selectedMedicines.length === 0) return true;
    if (e.medication === 'other') return selectedMedicines.includes(e.medicationType);
    return selectedMedicines.includes(e.medication);
  };
  const medicineFilterLabel = selectedMedicines.length === 0
    ? 'All Medicines'
    : selectedMedicines.map(medicineLabel).join(', ');

  // How many filters have been moved off their defaults (all medicines,
  // temperatures shown, the last RECORDS_DEFAULT_DAYS days; the child is
  // chosen in the banner, so it isn't counted here) —
  // surfaced as a badge on the Filters button. The date range counts when it's
  // been widened as much as when it's been narrowed: either way the list is no
  // longer showing what it shows by default, and the badge is what points at
  // the control responsible.
  const activeFilterCount =
    (selectedMedicines.length > 0 ? 1 : 0) +
    (hideTemperature ? 1 : 0) +
    (filterDays !== RECORDS_DEFAULT_DAYS ? 1 : 0);

  // Doses and temperature readings share one timeline here, newest first.
  const mergedEntries = [
    ...dosageHistory.map(e => ({ ...e, entryType: 'dose' })),
    ...temperatureLog.map(e => ({ ...e, entryType: 'temperature' })),
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const matchesEntryFilters = (e) => {
    if (filterChild !== 'all' && e.childId !== filterChild) return false;
    if (e.entryType === 'temperature') return showTemperature;
    return matchesMedicineFilter(e);
  };

  // null = "All time". An entry with an unparseable timestamp compares NaN here
  // and is kept rather than silently dropped — the list is the only place it
  // can still be spotted and corrected.
  const rangeCutoff = filterDays > 0 ? Date.now() - filterDays * MS_PER_DAY : null;
  const withinRange = (e) => rangeCutoff === null || !(new Date(e.timestamp).getTime() < rangeCutoff);

  const filteredHistory = mergedEntries.filter(e => matchesEntryFilters(e) && withinRange(e));
  // What the date range alone is holding back, so an empty (or short) list can
  // say "there's more, widen the range" instead of reading as "nothing here".
  const hiddenByRange = mergedEntries.filter(e => matchesEntryFilters(e) && !withinRange(e)).length;

  // Timeline chart data (same chart as the Temperature tab). It plots exactly
  // one child: a single temperature line can't represent two kids without
  // implying readings that never happened.
  //
  // The default subject is whichever child has the most recent entry — the one
  // being treated right now. Opening on the first child in the list instead
  // meant a child whose last dose was a week ago could hide the one being dosed
  // today, and dragged the chart's auto-range out to 7d with it.
  const lastEntryByChild = {};
  for (const e of [...dosageHistory, ...temperatureLog]) {
    const t = new Date(e.timestamp).getTime();
    if (!e.childId || isNaN(t)) continue;
    if (!(e.childId in lastEntryByChild) || t > lastEntryByChild[e.childId]) lastEntryByChild[e.childId] = t;
  }
  const childrenWithData = childrenList.filter(c => c.id in lastEntryByChild);
  const mostRecentlyActiveChildId = childrenWithData.reduce(
    (best, c) => (!best || lastEntryByChild[c.id] > lastEntryByChild[best] ? c.id : best), '');

  // The chart shows the banner's child, so it can never disagree with it (it
  // used to have its own child buttons). With "All children" it falls back to
  // whoever has the most recent entry, and names them in the header.
  const chartChildId = filterChild !== 'all' ? filterChild : mostRecentlyActiveChildId;
  const chartChildName = childrenList.find(c => c.id === chartChildId)?.name || '';

  const chartTemps = chartChildId ? temperatureLog.filter(e => e.childId === chartChildId) : [];
  const chartMeds = chartChildId ? dosageHistory.filter(e => e.childId === chartChildId) : [];

  // Export mirrors the on-screen child + medicine filters and the Display
  // Temperature toggle, narrowed to the last N days.
  const exportHistory = () => {
    const days = parseInt(exportDays, 10);
    if (!(days > 0)) { notify('Enter a valid number of days'); return; }
    const cutoff = Date.now() - days * MS_PER_DAY;
    const inRange = (e) => new Date(e.timestamp).getTime() >= cutoff;
    const inChild = (e) => filterChild === 'all' || e.childId === filterChild;

    const doseScope = dosageHistory
      .filter(e => inChild(e) && matchesMedicineFilter(e) && inRange(e))
      .map(e => ({ ...e, entryType: 'dose' }));
    const tempScope = showTemperature
      ? temperatureLog.filter(e => inChild(e) && inRange(e)).map(e => ({ ...e, entryType: 'temperature' }))
      : [];

    const scoped = [...doseScope, ...tempScope].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    if (scoped.length === 0) { notify('No entries in that range'); return; }

    const childLabel = filterChild === 'all' ? 'All Children' : (childrenList.find(c => c.id === filterChild)?.name || 'Unknown');
    const scopeLabel = `${medicineFilterLabel}${showTemperature ? ' + Temperature' : ''}`;
    const who = (e) => e.childName || (e.weight ? `${e.weight} kg` : '');
    const entryLabel = (e) => {
      if (e.entryType === 'temperature') return `${Number(e.tempC).toFixed(1)} °C`;
      if (e.medication === 'other') {
        return e.medicationName ? `${e.medicationType}: ${e.medicationName}` : e.medicationType;
      }
      const medLabel = e.medication.charAt(0).toUpperCase() + e.medication.slice(1);
      return `${medLabel} ${e.dose} ml (${e.mgTotal} mg, ${e.concentration})`;
    };

    // The columns every format shares, in one place so they can't drift apart.
    //
    // Who first, then when, then what: a doctor reads the export child by child,
    // and it's the order the Records cards already use. Type stays a column of
    // its own — it's what makes "show me only the temperatures" a filter in a
    // spreadsheet rather than a read-through.
    const COLUMN_HEADERS = ['Name', 'Date Time', 'Type', 'Medicine/Temperature'];
    const columns = (e) => [
      who(e),
      formatTimestamp(e.timestamp),
      e.entryType === 'temperature' ? 'Temperature' : 'Dose',
      entryLabel(e),
    ];
    // Standard CSV quoting, for whichever delimiter the format uses: a value
    // containing the delimiter, a quote or a newline gets wrapped, so a device
    // nickname or a free-text detail can't shift the columns.
    const escaper = (delim) => (v) => {
      const s = String(v ?? '');
      return s.includes(delim) || /["\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    let text;
    if (exportFormat === 'csv') {
      const esc = escaper(',');
      const rows = scoped.map(e => columns(e).map(esc).join(','));
      text = [COLUMN_HEADERS.join(','), ...rows].join('\n');
    } else if (exportFormat === 'md') {
      const rows = scoped.map(e => `| ${columns(e).map(v => v || '—').join(' | ')} |`);
      // Separator built from the header count, so adding or dropping a column
      // can't leave the table with the wrong number of dashes.
      const sep = `|${COLUMN_HEADERS.map(() => '---|').join('')}`;
      text = `**MedCalc History — ${childLabel} — ${scopeLabel} — last ${days} day${days === 1 ? '' : 's'}**\n\n`
        + `| ${COLUMN_HEADERS.join(' | ')} |\n${sep}\n${rows.join('\n')}`;
    } else {
      // Text is semicolon-separated so one export serves both readers: legible
      // pasted into a message, and parseable dropped into a spreadsheet.
      //
      // Semicolon rather than comma because the timestamps themselves contain
      // one ("Aug 15, 01:06 PM"), as do dose details ("213 mg, 120 mg/5 ml") —
      // with commas nearly every field would need quoting, which is precisely
      // what makes a CSV unpleasant to read. It's also the delimiter Excel
      // expects in much of Europe. No space after the delimiter: a spreadsheet
      // would keep it as part of the next field.
      const esc = escaper(';');
      const rows = scoped.map(e => columns(e).map(esc).join(';'));
      text = `MedCalc History — ${childLabel} — ${scopeLabel} — last ${days} day${days === 1 ? '' : 's'}\n\n`
        + [COLUMN_HEADERS.join(';'), ...rows].join('\n');
    }

    if (navigator.share) {
      navigator.share({ title: 'MedCalc History', text }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => notify('History copied to clipboard', { tone: 'info' })).catch(() => alert(text));
    } else {
      alert(text);
    }
  };

  // Deletes straight away and offers Undo, instead of an "Are you sure?" that
  // needs two hands. entryType is only this list's label for merged entries,
  // so it's stripped before the entry would go back into storage.
  const deleteWithUndo = (entry) => {
    const { entryType, ...stored } = entry;
    const isTemp = entryType === 'temperature';
    if (isTemp) onDeleteTemperature(entry.id); else onDelete(entry.id);
    const what = isTemp
      ? `${Number(entry.tempC).toFixed(1)} °C reading`
      : `${entry.medication === 'other' ? entry.medicationType : capitalizeMed(entry.medication)} dose`;
    notify(`Deleted ${what}`, {
      tone: 'info',
      action: { label: 'Undo', onClick: () => (isTemp ? onRestoreTemperature : onRestore)(stored) },
    });
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({
      childId: entry.childId || '',
      weight: entry.weight || '',
      when: toLocalDatetimeValue(new Date(entry.timestamp)),
      dose: entry.dose || '',
      medicationType: entry.medicationType || otherMedications[0]?.name || '',
      medicationName: entry.medicationName || '',
      tempC: entry.tempC ?? '',
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(null); };

  const saveEdit = (entry) => {
    const when = parseEnteredTime(editForm.when);
    if (!when) return;
    // Temperature readings carry no weight/dose, and a reading always belongs to
    // a named child, so they take their own (stricter) path rather than being
    // squeezed through the dose validation below.
    if (entry.entryType === 'temperature') {
      const child = childrenList.find(c => c.id === editForm.childId);
      if (!child) { notify('Select a valid child'); return; }
      const t = parseFloat(editForm.tempC);
      if (!(t >= TEMP_MIN && t <= TEMP_MAX)) {
        notify(`Enter a temperature between ${TEMP_MIN} and ${TEMP_MAX} °C`);
        return;
      }
      onUpdateTemperature(entry.id, {
        timestamp: when.toISOString(),
        tempC: t,
        childId: child.id,
        childName: child.name,
      });
      cancelEdit();
      return;
    }

    const child = editForm.childId ? childrenList.find(c => c.id === editForm.childId) : null;
    if (editForm.childId && !child) { notify('Select a valid child'); return; }
    if (!editForm.childId && !(parseFloat(editForm.weight) > 0)) { notify('Enter a valid weight'); return; }

    const updates = {
      timestamp: when.toISOString(),
      childId: editForm.childId,
      childName: child ? child.name : undefined,
      // The weight the dose was given at is part of the record: fixing only its
      // time mustn't overwrite it with the child's weight today. It changes
      // only when the dose is moved to a different child (or to a typed weight).
      weight: child
        ? (child.id === entry.childId && entry.weight ? entry.weight : child.weight)
        : parseFloat(editForm.weight),
    };

    if (entry.medication === 'other') {
      updates.medicationType = editForm.medicationType;
      updates.medicationName = editForm.medicationName.trim();
    } else {
      const dose = parseFloat(editForm.dose);
      if (!(dose > 0)) { notify('Enter a valid dose'); return; }
      // Recompute the mg total from the entry's concentration so ml and mg stay
      // in sync. If the concentration can't be parsed we can't derive mg, so
      // bail rather than save a dose whose mg total silently contradicts it.
      const parsed = parseStoredConcentration(entry.concentration);
      if (!parsed) { notify("Can't update this dose — its concentration is unreadable."); return; }
      updates.dose = dose.toFixed(1);
      updates.mgTotal = ((dose * parsed.mg) / parsed.ml).toFixed(0);
    }

    onUpdate(entry.id, updates);
    cancelEdit();
  };

  const inputCls = `w-full px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${
    darkMode ? 'bg-gray-600 border border-gray-500 text-white' : 'bg-white border border-gray-300 text-gray-900'
  }`;

  const getLastActionInfo = () => {
    if (undoStack.length === 0) return null;
    const lastAction = undoStack[undoStack.length - 1];
    const entry = lastAction.entry;
    const minutesAgo = Math.floor((Date.now() - lastAction.timestamp) / 60000);
    // entries is set when one Record logged several "Other" medicines at once.
    return { entry, entries: lastAction.entries, minutesAgo };
  };

  const lastAction = getLastActionInfo();

  return (
    <div className={`rounded-2xl shadow-xl p-4 sm:p-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
      {chartChildId && (chartTemps.length > 0 || chartMeds.length > 0) && (
        <div className="mb-4">
          {timelineOpen ? (
            /* key: a range pinned for one child is meaningless for the next, so
               switching subject remounts the chart and re-derives the range. */
            <TemperatureChart key={chartChildId} darkMode={darkMode} temps={chartTemps} meds={chartMeds}
              subtitle={chartChildName} onCollapse={() => setTimelineOpen(false)} />
          ) : (
            <button onClick={() => setTimelineOpen(true)}
              className={`w-full flex items-center justify-between px-4 py-2 rounded-lg border font-medium transition ${
                darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
              }`}>
              <span>Timeline{chartChildName && <span className={`font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}> · {chartChildName}</span>}</span>
              <span className="text-base leading-none">⌄</span>
            </button>
          )}
        </div>
      )}

      {lastAction && (
        <div className={`mb-4 p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                Last registered dose ({lastAction.minutesAgo}m ago):
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {lastAction.entry.childName && `${lastAction.entry.childName} - `}
                {lastAction.entry.medication === 'other'
                  /* Several "Other" medicines can be logged in one action, and
                     Undo takes back all of them — so name all of them. */
                  ? (lastAction.entries || [lastAction.entry]).map(e => e.medicationType).join(' + ')
                  : `${lastAction.entry.medication} ${lastAction.entry.dose}ml (${lastAction.entry.mgTotal}mg)`}
              </p>
            </div>
            <button
              onClick={onUndo}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all ${
                darkMode 
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white'
              }`}>
              <X className="w-4 h-4 flex-shrink-0" />
              Undo
            </button>
          </div>
        </div>
      )}

      <div className="mb-4">
        <button onClick={() => setShowFilters(v => !v)}
          className={`w-full flex items-center justify-between px-4 py-2 rounded-lg font-medium transition ${
            darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}>
          <span className="flex items-center gap-2">
            Filters
            {activeFilterCount > 0 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-600 text-white">{activeFilterCount}</span>
            )}
          </span>
          <span className={`text-base leading-none transition-transform ${showFilters ? 'rotate-180' : ''}`}>⌄</span>
        </button>

        {showFilters && (
          <div className={`mt-3 space-y-4 p-4 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Date Range
              </label>
              <select value={filterDays} onChange={(e) => setFilterDays(Number(e.target.value))}
                className={`w-full px-3 py-2 sm:px-4 sm:py-3 rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  darkMode ? 'bg-gray-700 border border-gray-600 text-white' : 'bg-white border border-gray-300 text-gray-900'
                }`}>
                {RECORDS_RANGE_OPTIONS.map(d => (
                  <option key={d} value={d}>{recordsRangeLabel(d)}</option>
                ))}
              </select>
              <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                How far back the list reaches. Nothing is deleted — widening the range brings older entries back. Export has its own range below.
              </p>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Filter by Medicine
              </label>
              <div className="space-y-3">
                {Object.entries(medicineGroups).sort(([a], [b]) => a.localeCompare(b)).map(([category, meds]) => {
                  const keys = meds.map(m => m.key);
                  const allSelected = keys.every(k => selectedMedicines.includes(k));
                  const someSelected = keys.some(k => selectedMedicines.includes(k));
                  return (
                    <div key={category}>
                      <label className={`flex items-center gap-2 text-sm font-semibold cursor-pointer mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        <input type="checkbox" checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => toggleCategory(keys)}
                          className="w-4 h-4 flex-shrink-0 accent-indigo-600" />
                        {category}
                      </label>
                      <div className="flex flex-wrap gap-2 pl-6">
                        {meds.map(({ key, label }) => {
                          const checked = selectedMedicines.includes(key);
                          return (
                            <label key={key}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer border transition ${
                                checked
                                  ? 'bg-indigo-600 border-indigo-600 text-white'
                                  : darkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                              }`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleMedicine(key)} className="hidden" />
                              {label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Check a category to select all its medicines, or pick individual ones. Leave everything unchecked to show every medicine.
              </p>
            </div>

            <label className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <input type="checkbox" checked={hideTemperature}
                onChange={(e) => setHideTemperature(e.target.checked)}
                className="w-4 h-4 flex-shrink-0 accent-indigo-600" />
              Hide Temperature
            </label>
          </div>
        )}
      </div>

      {filteredHistory.length === 0 ? (
        <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <Book className="w-16 h-16 mx-auto mb-4 opacity-50" />
          {/* An empty list with older entries sitting just outside the range is
              the one case where "No records yet" would be a lie — and the most
              alarming one, since it reads as lost data. */}
          <p>{hiddenByRange > 0 ? `No records in the ${recordsRangeLabel(filterDays).toLowerCase()}` : 'No records yet'}</p>
          {hiddenByRange > 0 && (
            <p className="text-sm mt-1">
              {hiddenByRange} older {hiddenByRange === 1 ? 'entry is' : 'entries are'} outside the date range — widen it under Filters.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/* Grouped under day headings, so each card only needs its time. */}
          {filteredHistory.map((entry, i) => [
            (i === 0 || dayKey(filteredHistory[i - 1].timestamp) !== dayKey(entry.timestamp)) && (
              <h4 key={`day-${dayKey(entry.timestamp)}`}
                className={`text-xs font-semibold uppercase tracking-wide px-1 ${i === 0 ? '' : 'pt-3'} ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {dayHeading(entry.timestamp)}
              </h4>
            ),
            entry.entryType === 'temperature' && editingId === entry.id ? (
              <div key={entry.id} className={`rounded-lg p-3 border space-y-3 ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                {childrenList.length > 0 && (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Child</label>
                    <select value={editForm.childId}
                      onChange={(e) => setEditForm({ ...editForm, childId: e.target.value })}
                      className={inputCls}>
                      <option value="">-- Select a child --</option>
                      {childrenList.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.weight} kg)</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Temperature (°C)</label>
                  <input type="number" step="0.1" min={TEMP_MIN} max={TEMP_MAX} inputMode="decimal"
                    value={editForm.tempC}
                    onChange={(e) => setEditForm({ ...editForm, tempC: e.target.value })}
                    className={inputCls} />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date & Time</label>
                  <div className="flex gap-2">
                    <input type="datetime-local" value={editForm.when}
                      onChange={(e) => setEditForm({ ...editForm, when: e.target.value })}
                      className={inputCls} />
                    <button type="button" onClick={() => setEditForm({ ...editForm, when: toLocalDatetimeValue(new Date()) })}
                      className={`px-3 py-2 rounded-lg text-sm font-medium flex-shrink-0 ${
                        darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}>
                      Now
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(entry)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition">
                    <Save className="w-4 h-4 flex-shrink-0" /> Save
                  </button>
                  <button onClick={cancelEdit}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium transition ${
                      darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}>
                    <X className="w-4 h-4 flex-shrink-0" /> Cancel
                  </button>
                </div>
              </div>
            ) : entry.entryType === 'temperature' ? (
              <div key={entry.id} className={`rounded-lg px-3 py-2 border flex items-center justify-between gap-2 ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="min-w-0 flex-1">
                  {/* When it happened rides on the name line, in the same
                      full-strength colour: it's the entry's identity, not a
                      footnote. The relative "3 hours ago" takes its old place
                      below, where the muted treatment belongs. */}
                  <div className={`flex items-baseline justify-between gap-2 pb-1 mb-1 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <span className={`font-semibold text-sm truncate ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {entry.childName || 'Temperature'}
                    </span>
                    <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {formatTimePart(entry.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div className="flex items-baseline gap-x-2 flex-wrap">
                      {/* The colour carries "fever" on its own — amber at 38,
                          red at 39 — so the word was saying it twice. */}
                      <span className={`text-sm font-medium ${
                        entry.tempC >= 39 ? 'text-red-500' : entry.tempC >= 38 ? 'text-orange-500' : (darkMode ? 'text-green-400' : 'text-green-700')
                      }`}>
                        {Number(entry.tempC).toFixed(1)} °C
                      </span>
                      {/* Alongside the reading rather than on its own line: a
                          temperature card has one short value where a dose has
                          three, so there's room for it on the same row. At
                          phone width the row still wraps the elapsed time onto
                          a second line — exactly as the dose card has always
                          done — rather than clipping anything. Readings logged
                          before the stamp existed show nothing here. */}
                      {entry.loggedBy && (
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {loggedByLabel(entry.loggedBy)}
                        </span>
                      )}
                    </div>
                    <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatAgo(entry.timestamp)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(entry)}
                    aria-label="Edit reading"
                    className={`p-1.5 rounded-lg transition ${
                      darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                    }`}>
                    <Pencil className="w-4 h-4 flex-shrink-0" />
                  </button>
                  <button onClick={() => deleteWithUndo(entry)}
                    aria-label="Delete reading"
                    className={`p-1.5 rounded-lg transition ${
                      darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                    }`}>
                    <Trash2 className="w-4 h-4 flex-shrink-0" />
                  </button>
                </div>
              </div>
            ) : editingId === entry.id ? (
              <div key={entry.id} className={`rounded-lg p-3 border space-y-3 ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                {childrenList.length > 0 && (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Child</label>
                    <select value={editForm.childId}
                      onChange={(e) => setEditForm({ ...editForm, childId: e.target.value })}
                      className={inputCls}>
                      <option value="">No child (manual weight)</option>
                      {childrenList.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.weight} kg)</option>
                      ))}
                    </select>
                  </div>
                )}

                {!editForm.childId && (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Weight (kg)</label>
                    <input type="number" step="0.1" value={editForm.weight}
                      onChange={(e) => setEditForm({ ...editForm, weight: e.target.value })}
                      className={inputCls} />
                  </div>
                )}

                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date &amp; Time</label>
                  <div className="flex gap-2">
                    <input type="datetime-local" value={editForm.when}
                      onChange={(e) => setEditForm({ ...editForm, when: e.target.value })}
                      className={`flex-1 min-w-0 ${inputCls}`} />
                    <button type="button" onClick={() => setEditForm({ ...editForm, when: toLocalDatetimeValue(new Date()) })}
                      className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition ${
                        darkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}>
                      Now
                    </button>
                  </div>
                </div>

                {entry.medication === 'other' ? (
                  <>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Medication Name</label>
                      <select value={editForm.medicationType}
                        onChange={(e) => setEditForm({ ...editForm, medicationType: e.target.value })}
                        className={inputCls}>
                        {/* A name since renamed or removed in Settings stays an
                            option, or the picker would show the first medicine
                            while still saving the old one. */}
                        {[...new Set([editForm.medicationType, ...otherMedications.map(m => m.name)].filter(Boolean))]
                          .sort((a, b) => a.localeCompare(b)).map(name => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Details (Optional)</label>
                      <input type="text" value={editForm.medicationName}
                        onChange={(e) => setEditForm({ ...editForm, medicationName: e.target.value })}
                        className={inputCls} />
                    </div>
                  </>
                ) : (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Dose (ml) — {entry.medication}, {entry.concentration}
                    </label>
                    <input type="number" step="0.1" value={editForm.dose}
                      onChange={(e) => setEditForm({ ...editForm, dose: e.target.value })}
                      className={inputCls} />
                  </div>
                )}

                <div className="flex gap-2">
                  <button onClick={() => saveEdit(entry)}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 rounded-lg transition text-sm">
                    Save
                  </button>
                  <button onClick={cancelEdit}
                    className={`flex-1 font-medium py-2 rounded-lg transition text-sm ${
                      darkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div key={entry.id} className={`rounded-lg px-3 py-2 border flex items-center justify-between gap-2 ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="min-w-0 flex-1">
                  {/* See the temperature card above: stamp on the name line at
                      full strength, "3 hours ago" in its old muted slot. */}
                  <div className={`flex items-baseline justify-between gap-2 pb-1 mb-1 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <span className={`font-semibold text-sm truncate ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {entry.childName || `${entry.weight} kg`}
                    </span>
                    <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {formatTimePart(entry.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div className="flex items-baseline gap-x-2 flex-wrap">
                      {entry.medication === 'other' ? (
                        <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {entry.medicationType}
                        </span>
                      ) : (
                        <>
                          <span className={`text-sm font-medium capitalize ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                            {entry.medication}
                          </span>
                          <span className={`text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                            {entry.dose} ml
                          </span>
                        </>
                      )}
                    </div>
                    <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatAgo(entry.timestamp)}
                    </span>
                  </div>
                  {(() => {
                    const detailsLine = entry.medication === 'other'
                      ? [entry.medicationName, loggedByLabel(entry.loggedBy)].filter(Boolean).join(' • ')
                      : [`${entry.mgTotal} mg`, entry.concentration, loggedByLabel(entry.loggedBy)].filter(Boolean).join(' • ');
                    return detailsLine && (
                      <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {detailsLine}
                      </p>
                    );
                  })()}
                </div>
                <button onClick={() => startEdit(entry)}
                  aria-label="Edit entry"
                  className={`p-1.5 rounded-lg transition flex-shrink-0 ${
                    darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                  }`}>
                  <Pencil className="w-4 h-4 flex-shrink-0" />
                </button>
                <button onClick={() => deleteWithUndo(entry)}
                  aria-label="Delete entry"
                  className={`p-1.5 rounded-lg transition flex-shrink-0 ${
                    darkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                  }`}>
                  <Trash2 className="w-4 h-4 flex-shrink-0" />
                </button>
              </div>
            )
          ])}
          {/* The list ends where the range does, not where the data does —
              without this the last card looks like the oldest record there is. */}
          {hiddenByRange > 0 && (
            <p className={`text-xs text-center pt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Showing the {recordsRangeLabel(filterDays).toLowerCase()} · {hiddenByRange} older{' '}
              {hiddenByRange === 1 ? 'entry' : 'entries'} hidden — change the date range under Filters.
            </p>
          )}
        </div>
      )}

      <div className={`rounded-lg p-3 mt-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <input type="number" min="1" value={exportDays} onChange={(e) => setExportDays(e.target.value)}
            aria-label="Days"
            className={`w-14 flex-shrink-0 px-2 py-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${
              darkMode ? 'bg-gray-600 border border-gray-500 text-white' : 'bg-white border border-gray-300 text-gray-900'
            }`} />
          <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>days</span>
          <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}
            aria-label="Export format"
            className={`flex-shrink-0 px-2 py-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${
              darkMode ? 'bg-gray-600 border border-gray-500 text-white' : 'bg-white border border-gray-300 text-gray-900'
            }`}>
            <option value="text">Text</option>
            <option value="csv">CSV</option>
            <option value="md">Markdown</option>
          </select>
          <button onClick={exportHistory}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg transition text-sm font-medium">
            Export
          </button>
        </div>
      </div>
    </div>
  );
}
